import { Component, computed, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { debounceTime } from 'rxjs';

import { FileVersionCommentModel } from '@files/attachments-note/attachments-note/attachments-note.model';
import { ModuleInstanceRequestParams } from '@models/api';
import { AttachmentModel, MimeTypesEnum } from '@models/module/fields/main';
import { ModuleInstanceService } from '@module-instance/services';
import { CipoAttachmentComponent } from '@shared/components/fields/cipo-attachment/cipo-attachment.component';
import { CipoImgBase64 } from '@shared/components/img-base64/img-base64.component';
import { DEBOUNCE_TIMES } from '@shared/consts';
import { CipoDialogModule, CipoFormModule } from '@shared/modules';
import { FileService, NotificationService } from '@shared/services';

@Component({
  selector: 'cipo-attachments-note',
  imports: [CipoDialogModule, CipoFormModule, CipoAttachmentComponent, CipoImgBase64, NgxSkeletonLoaderModule],
  templateUrl: './attachments-note.component.html',
  styleUrls: ['./attachments-note.component.scss'],
})
export class AttachmentsNoteComponent implements OnInit {
  moduleInstanceService = inject(ModuleInstanceService);
  notificate = inject(NotificationService);
  translate = inject(TranslateService);
  private fileService = inject(FileService);

  skeleton = signal(Array(5));
  loading = signal<boolean>(false);
  files = signal<FileVersionCommentModel[]>([]);
  @Input('entityinstanceid') entityInstanceId: number;
  @Input('fieldid') fieldId: number;
  @Input('onlyview') onlyView: boolean;
  @Input('isdraft') isDraft: boolean;
  @Input('parententityinstanceid') parentEntityInstanceId: number;
  @Input('attachments') attachments: AttachmentModel[];
  @Output('onclose') onClose: EventEmitter<FileVersionCommentModel[]> = new EventEmitter<FileVersionCommentModel[]>();

  searchControl = new FormControl('');
  searchValue = signal('');
  filteredFiles = computed(() => {
    const searchValue = this.searchValue();
    return this.files()?.filter(
      o =>
        o.name?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
        o.comment?.toLowerCase()?.includes(searchValue?.toLowerCase()),
    );
  });

  filesAsAttachments = computed<AttachmentModel[]>(() =>
    this.filteredFiles().map(f => ({
      id: f.fileId,
      versionId: f.versionId,
      name: f.name,
      mimeType: f.mimeType as MimeTypesEnum,
      thumbnail50: f.thumbnail50,
      thumbnail200: f.thumbnail200,
      note: f.comment,
      version: undefined,
      parentId: undefined,
    })),
  );

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(DEBOUNCE_TIMES.short))
      .subscribe(val => this.searchValue.set(val));

    // get files
    if (this.entityInstanceId) {
      this.loading.set(true);

      const params: ModuleInstanceRequestParams = {
        entityInstanceId: this.entityInstanceId,
        fieldId: this.fieldId,
        parentEntityInstanceId: this.parentEntityInstanceId,
      };

      this.moduleInstanceService.getAttachmentsNote(params).subscribe({
        next: f => {
          this.files.set(f);
          this.addMissingAttachments();
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
        },
      });
    } else {
      this.addMissingAttachments();
    }
  }

  addMissingAttachments() {
    if (!this.attachments?.length) {
      return;
    }
    this.files.update(currentFiles => {
      return this.attachments.map(att => {
        const existing = currentFiles.find(f => f.fileId === att.id);

        if (existing) {
          return {
            ...existing,
            name: att.name,
            versionId: att.versionId,
            mimeType: att.mimeType,
            thumbnail50: att.thumbnail50 || existing.thumbnail50,
            thumbnail200: att.thumbnail200 || existing.thumbnail200,
            comment: existing.comment || att.note,
          };
        }

        return {
          fileId: att.id,
          versionId: att.versionId,
          name: att.name,
          mimeType: att.mimeType,
          thumbnail50: att.thumbnail50,
          thumbnail200: att.thumbnail200,
          fieldInstanceId: null,
          comment: att.note,
          notAutoSaved: false,
        };
      });
    });
  }

  clearInput() {
    this.searchControl.setValue('');
  }

  onCloseClick() {
    this.onClose.emit(this.files());
  }

  noteChanged(file: FileVersionCommentModel) {
    file.notAutoSaved = true;
  }

  openFile(index: number) {
    // WI-8965: attachments on CIPO documents must always open read-only when document is not in draft mode
    // to preserve document content and prevent unauthorised edits through the WOPI viewer.
    const readOnly = !this.isDraft;
    this.fileService.openFile(this.filesAsAttachments(), index, readOnly);
  }
}
